import { AfterViewInit, Component, ElementRef, HostBinding, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, combineLatest, filter, firstValueFrom, takeUntil } from 'rxjs';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { MailAccountsService } from '../../comunications/_services/mail-account.service';
import { MailAccount } from '../../comunications/_types/mail-account';
import { LoginService } from '../../login/login.service';
import { MailboxService } from '../_services/mailbox.service';
import { ComposeDialogComponent } from '../compose-dialog/compose-dialog.component';
import {
  MailboxAttachment,
  MailboxComposeMode,
  MailboxError,
  MailboxFolder,
  MailboxMessage,
  MailboxMessageSummary,
  MailboxSearch,
  MailboxThread,
  MailboxThreadState,
  folderDisplayName
} from '../_types/mailbox';

/// Pantalla del buzón: tres paneles (carpetas, listado y lectura).
///
/// Este componente es el **único dueño de la URL**: los hijos emiten eventos y aquí se navega. Así se evita el
/// bucle clásico de "cargo, navego, se recarga" que aparece cuando varios componentes escriben en la ruta.
///
/// Qué va en la URL y por qué: la cuenta, la carpeta y el mensaje van en la ruta, de modo que un correo concreto
/// se puede enlazar y el botón de atrás lo cierra. La búsqueda y la paginación van en parámetros de consulta,
/// porque son volátiles. Cambiar de mensaje con el teclado reemplaza la entrada del historial, para no dejar
/// cuarenta entradas al recorrer una bandeja.
@Component({
    selector: 'app-mailbox-page',
    templateUrl: './mailbox-page.component.html',
    styleUrls: ['./mailbox-page.component.css'],
    standalone: false
})
export class MailboxPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  /// La cuenta abierta. No hay selector: al buzón se entra desde la lista de buzones, y cambiar de cuenta a
  /// mitad de la pantalla solo servía para confundir. Se carga para poder titular el card con el nombre y la
  /// dirección.
  public account: MailAccount | null = null;
  public mailAccountId: string | null = null;

  public folders: MailboxFolder[] = [];
  public foldersLoading: boolean = false;

  /// Diálogo de administración de carpetas abierto.
  public foldersManagerVisible: boolean = false;

  /// Compositor. Se accede por referencia porque se abre con datos, no con un simple booleano.
  @ViewChild('composer') composer?: ComposeDialogComponent;

  /// Preparando el borrador en el servidor.
  public draftLoading: boolean = false;

  public folderPath: string = 'INBOX';
  public uidValidity: number = 0;

  public messages: MailboxMessageSummary[] = [];
  public totalRecords: number = 0;
  public first: number = 0;
  public rows: number = 50;
  public listLoading: boolean = false;

  /// Si se ofrece la vista por conversaciones.
  ///
  /// Está **oculta a propósito**: el agrupado funciona (clave de conversación en el listado y hilo completo al
  /// desplegar), pero se pidió dejarlo fuera de la vista por ahora. Poner a true para volver a ofrecerlo.
  ///
  /// El interruptor no solo esconde el botón: también hace que se ignore la preferencia guardada. Sin eso, quien
  /// hubiera dejado el agrupado activado se quedaría con el listado agrupado y sin botón para desactivarlo.
  public static readonly CONVERSATIONS_ENABLED = false;

  public readonly conversationsEnabled = MailboxPageComponent.CONVERSATIONS_ENABLED;

  /// Ver el listado agrupado por conversaciones. Se recuerda entre visitas porque es una preferencia de cómo se
  /// quiere trabajar, no algo de esta pantalla concreta.
  public grouped: boolean =
    MailboxPageComponent.CONVERSATIONS_ENABLED && localStorage.getItem('mailbox.grouped') === '1';

  /// Conversaciones ya completadas contra el servidor, por clave.
  ///
  /// Se sustituye el Map entero en cada cambio, no se muta: es lo que hace que el listado se entere.
  public conversations: ReadonlyMap<string, MailboxThreadState> = new Map();

  public selectedUid: number | null = null;
  public message: MailboxMessage | null = null;
  public messageLoading: boolean = false;
  public bodyDocument: string | null = null;
  public bodyUrl: string | null = null;
  public sourceUrl: string | null = null;
  public allowRemoteImages: boolean = false;

  public search: MailboxSearch = {};
  public searchText: string = '';

  public allowManage: boolean = false;
  public allowSend: boolean = false;

  /// Errores del buzón, por panel. Se pintan dentro del panel afectado y no como avisos flotantes: un buzón caído
  /// no debe llenar la esquina de la pantalla ni tapar el resto de la aplicación.
  public folderError: MailboxError | null = null;
  public listError: MailboxError | null = null;
  public messageError: MailboxError | null = null;

  /// Alto disponible, calculado. Ver `measureHeight`.
  ///
  /// Se aplica al host y no al card: es lo que hace que el card tenga un alto real que respetar, y que esta
  /// pantalla no pueda desplazar el documento.
  @HostBinding('style.height')
  public availableHeight: string = '70vh';

  /// Cuánto se sigue midiendo tras pintar, mientras el armazón de la aplicación acaba de montarse.
  ///
  /// Con holgura sobre lo que tarda: `LoginService.checkSavedToken` avisa de la sesión en el siguiente turno, pero
  /// esto no depende de ese detalle a propósito, para que no vuelva a romperse si allí cambia el momento. Son unos
  /// 70 fotogramas leyendo la posición del elemento, que no se nota.
  private static readonly SHELL_SETTLE_MS = 1200;

  /// Huella de lo que determina el listado (cuenta, carpeta, página y búsqueda).
  ///
  /// Existe porque la suscripción a la ruta salta también cuando solo cambia el mensaje abierto, y recargar el
  /// listado al abrir cada correo sería un viaje al servidor de correo por cada clic, para nada.
  private listKey: string | null = null;

  /// Vigilante del alto del armazón. Ver `observeChrome`.
  private chromeObserver?: ResizeObserver;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private host: ElementRef<HTMLElement>,
    private topbarService: TopbarService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private loginService: LoginService,
    private mailAccountsService: MailAccountsService,
    private mailboxService: MailboxService
  ) {}

  ngOnInit() {
    this.allowManage = this.loginService.hasPermission('MAILBOX.MANAGE');
    this.allowSend = this.loginService.hasPermission('MAILBOX.SEND');

    //la ruta de navegación se reconstruye al terminar cada navegación, porque el servicio la vacía en ese momento y
    //aquí se cambia de carpeta sin recrear el componente
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(() => this.applyBreadcrumb());

    this.applyBreadcrumb();

    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([params, query]) => {
        const accountId = params.get('accountId');
        const folder = params.get('folder');
        const uid = params.get('uid');

        const accountChanged = accountId !== this.mailAccountId;

        this.mailAccountId = accountId;
        if (folder) this.folderPath = decodeURIComponent(folder);

        this.rows = Number(query.get('rows') ?? this.rows) || 50;
        const page = Number(query.get('page') ?? 1) || 1;
        this.first = (page - 1) * this.rows;

        this.searchText = query.get('q') ?? '';
        this.search = {
          text: this.searchText || undefined,
          unseenOnly: query.get('unseen') === '1',
          flaggedOnly: query.get('flagged') === '1',
          searchBody: query.get('body') === '1'
        };

        if (!accountId) {
          this.resolveAccountAndRedirect();
          return;
        }

        if (accountChanged) {
          this.folders = [];
          this.account = null;
          this.loadAccount(accountId!);
          this.loadFolders();
        }

        const listKey = JSON.stringify([accountId, this.folderPath, page, this.rows, this.search]);
        if (listKey !== this.listKey) {
          this.listKey = listKey;
          this.loadMessages();
        }

        this.selectedUid = uid ? Number(uid) : null;
        if (this.selectedUid) this.loadMessage(this.selectedUid);
        else this.clearMessage();
      });
  }

  ngAfterViewInit() {
    this.stabilizeHeight();

    //las tipografías cambian el alto de la barra superior al cargar, y eso mueve el card hacia abajo
    (document as any).fonts?.ready?.then(() => this.measureHeight());
  }

  ngOnDestroy() {
    this.chromeObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  onResize() {
    //también se intenta enganchar el vigilante: si la barra superior tardó más que la ventana inicial en aparecer,
    //esta es la ocasión de recuperarlo
    this.observeChrome();
    this.measureHeight();
  }

  /// Mide fotograma a fotograma mientras el armazón de la aplicación se monta, y en cuanto puede deja el trabajo
  /// en manos de un vigilante.
  ///
  /// Al recargar la página con el buzón abierto, el armazón se pinta por partes: la barra superior y la barra
  /// lateral cuelgan de `is_user_logged`, y la banda de «entorno de pruebas» aparece aparte. Cuando la barra
  /// superior se monta, el card baja, y si ya se había medido, se sale de la pantalla por abajo hasta que se
  /// redimensiona la ventana (que es lo único que volvía a medir).
  ///
  /// La versión anterior paraba en cuanto el valor se repetía cinco fotogramas, unos 80 ms: paraba **antes** de que
  /// la barra superior existiera, así que no llegaba a ver el desplazamiento. Ahora no se para por estabilidad
  /// —que en esos primeros instantes es engañosa— sino al agotar una ventana con holgura de sobra.
  ///
  /// No se usa un ResizeObserver sobre el body: eso ya se probó y era un bucle, porque el contenido del propio card
  /// disparaba la remedición. El alto depende solo de lo que hay ENCIMA del card, y eso es lo que se vigila.
  private stabilizeHeight() {
    const inicio = performance.now();

    const paso = () => {
      this.measureHeight();
      this.observeChrome();

      if (performance.now() - inicio < MailboxPageComponent.SHELL_SETTLE_MS) requestAnimationFrame(paso);
    };

    requestAnimationFrame(paso);
  }

  /// Vigila el alto de lo que hay por encima del card, que es lo único que determina dónde empieza.
  ///
  /// Es lo que mantiene el alto correcto pasada la ventana inicial: la barra superior crece o se encoge por su
  /// cuenta (las tipografías al cargar, un breadcrumb que pasa a dos líneas, los contadores que aparecen), y sin
  /// esto solo se recalculaba al redimensionar la ventana.
  ///
  /// No puede realimentarse: el card no influye en el alto de la barra superior ni en el de la banda de aviso.
  private observeChrome() {
    if (this.chromeObserver) return;

    //la barra superior es la que manda; hasta que exista no merece la pena montar el vigilante
    const topbar = document.querySelector('app-topbar');
    if (topbar == null) return;

    this.chromeObserver = new ResizeObserver(() => this.measureHeight());
    this.chromeObserver.observe(topbar);

    const banda = document.querySelector('.top-banner-danger');
    if (banda != null) this.chromeObserver.observe(banda);
  }

  /// Calcula el alto disponible en lugar de usar `100vh`.
  ///
  /// El armazón de la aplicación no tiene alto fijo: usa `min-height: 100vh` con desplazamiento del documento, y
  /// fuera de producción añade además una banda de aviso. Con `100vh` la pantalla se saldría por abajo, y
  /// `scrollHeight="flex"` de PrimeNG no funciona sin un ancestro con alto. Medir la posición real del componente
  /// es lo único que aguanta que aparezca o desaparezca la banda.
  ///
  /// La distancia se toma **respecto al documento** (`+ window.scrollY`) y no respecto al viewport: si la pantalla
  /// ya está desplazada, el rectángulo del elemento devuelve un valor menor que el real y el alto saldría de más,
  /// justo el error que hace que la pantalla se desplace todavía un poco más.
  private measureHeight() {
    const top = this.host.nativeElement.getBoundingClientRect().top + window.scrollY;

    //el 1.5rem es el relleno inferior del armazón (la clase p-4 de app.component)
    const height = `calc(100vh - ${Math.round(top)}px - 1.5rem)`;
    if (height !== this.availableHeight) this.availableHeight = height;
  }

  // ------------------------------------------------------------- cuentas

  /// Sin cuenta en la ruta: se resuelve la última usada, o la primera disponible.
  private async resolveAccountAndRedirect() {
    //mismo filtro que la pantalla de buzones: solo las cuentas con IMAP y marcadas como visibles
    const params: IParamsCollection = {
      sort: ['name'],
      filter: FilterBuilder.fromObject({
        imapEnabled: new Filter(FilterOperation.equals, 'imapEnabled', '', true),
        allowMailboxViewer: new Filter(FilterOperation.equals, 'allowMailboxViewer', '', true)
      }).build()
    };

    try {
      const response = await firstValueFrom(this.mailAccountsService.all(params));
      const accounts: MailAccount[] = response.data;
      if (accounts.length === 0) return;

      const remembered = localStorage.getItem('mailbox.lastAccountId');
      const preferred = accounts.find(account => account.id === remembered) ?? accounts[0];

      this.router.navigate(['/mailbox', preferred.id, encodeURIComponent('INBOX')], { replaceUrl: true });
    } catch {
      //sin cuentas no hay nada que mostrar; la pantalla se queda en su estado vacío
    }
  }

  /// Datos de la cuenta abierta, solo para titular el card.
  private async loadAccount(accountId: string) {
    localStorage.setItem('mailbox.lastAccountId', accountId);
    try {
      this.account = await firstValueFrom(this.mailAccountsService.get(accountId));
    } catch {
      this.account = null;
    }
    this.applyBreadcrumb();
  }

  /// Dirección de la cuenta, que es lo que identifica el buzón para quien lo mira.
  get accountAddress(): string {
    return this.account?.smtpFrom || this.account?.username || '';
  }

  /// Ruta de navegación: desde la lista de buzones hasta la carpeta abierta.
  private applyBreadcrumb() {
    const items: {label: string, url: string}[] = [{ label: 'Buzones de correo', url: '/mailboxes' }];

    if (this.account?.name && this.mailAccountId) {
      items.push({ label: this.account.name, url: `/mailbox/${this.mailAccountId}` });
    }

    //la ruta acaba en la carpeta, y el mensaje abierto no añade tramo: es un detalle del panel, no un nivel de
    //navegación.
    //
    //La carpeta se pone aquí con SU enlace, y se le dice al servicio que no añada su tramo automático. Ese tramo
    //apunta siempre a la ruta actual, así que con un mensaje abierto pulsar «Entrada» no llevaba a la entrada sino
    //al correo que ya estaba delante.
    if (this.mailAccountId) {
      items.push({
        label: this.currentFolderLabel(),
        url: `/mailbox/${this.mailAccountId}/${encodeURIComponent(this.folderPath)}`
      });
    }

    this.topbarService.setBreadcrumb(items, false);

    //el título de la pantalla sigue siendo la carpeta, aunque ya no forme parte de la ruta de navegación
    this.topbarService.setTitle(this.currentFolderLabel());
  }

  /// Nombre visible de la carpeta abierta, traducido igual que en el árbol.
  private currentFolderLabel(): string {
    const buscar = (list: MailboxFolder[]): MailboxFolder | null => {
      for (const folder of list ?? []) {
        if (folder.path === this.folderPath) return folder;
        const encontrada = buscar(folder.children ?? []);
        if (encontrada) return encontrada;
      }
      return null;
    };

    const folder = buscar(this.folders);
    if (folder) return folderDisplayName(folder.name, folder.specialUse);

    //el árbol puede no haber llegado todavía: se saca del último tramo de la ruta
    const segments = this.folderPath.split(/[\/.]/);
    return folderDisplayName(segments[segments.length - 1] || this.folderPath);
  }

  // ------------------------------------------------------------- carpetas

  openFoldersManager() {
    this.foldersManagerVisible = true;
  }

  loadFolders() {
    if (!this.mailAccountId) return;

    this.foldersLoading = true;
    this.folderError = null;

    this.mailboxService
      .getFolders(this.mailAccountId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: folders => {
          this.folders = folders;
          this.foldersLoading = false;
          this.ensureVisibleFolder();
          this.applyBreadcrumb();
        },
        error: (error: MailboxError) => {
          this.folderError = error;
          this.foldersLoading = false;
        }
      });
  }

  /// Si la carpeta de la URL ya no está en el árbol, se cae a la de entrada.
  ///
  /// Pasa de verdad: un enlace guardado a una carpeta que después se ha desuscrito dejaría la pantalla pidiendo
  /// mensajes de algo que el usuario ya no ve ni puede volver a seleccionar.
  private ensureVisibleFolder() {
    if (this.folders.length === 0) return;

    const exists = (list: MailboxFolder[]): boolean =>
      list.some(folder => folder.path === this.folderPath || exists(folder.children ?? []));

    if (exists(this.folders)) return;

    const inbox = this.folders.find(folder => folder.specialUse === 'inbox') ?? this.folders[0];
    this.router.navigate(['/mailbox', this.mailAccountId, encodeURIComponent(inbox.path)], { replaceUrl: true });
  }

  onFolderSelected(folder: MailboxFolder) {
    if (folder.path === this.folderPath) return;
    this.router.navigate(['/mailbox', this.mailAccountId, encodeURIComponent(folder.path)]);
  }

  // ------------------------------------------------------------- conversaciones

  toggleGrouped() {
    this.grouped = !this.grouped;
    localStorage.setItem('mailbox.grouped', this.grouped ? '1' : '0');
    this.conversations = new Map();
  }

  /// Se ha desplegado una conversación: se le pide al servidor el hilo completo, porque el listado solo agrupa lo
  /// que trae la página y el resto de los mensajes puede estar en otra.
  onConversationToggled(thread: MailboxThread) {
    if (!this.mailAccountId) return;

    const estado = this.conversations.get(thread.key);

    //ya está pedida o ya está: plegar y desplegar no debe repetir el viaje
    if (estado?.loading || estado?.messages) return;

    this.setThreadState(thread.key, { loading: true, truncated: false, messages: null, error: null });

    this.mailboxService
      .getConversation(this.mailAccountId, this.folderPath, thread.newest.uid, this.uidValidity)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: conversation => this.setThreadState(thread.key, {
          loading: false,
          truncated: conversation.truncated,
          messages: conversation.messages,
          error: null
        }),
        error: (error: MailboxError) => {
          //la conversación se queda con lo que había en la página, que es mejor que nada, y se avisa sin tocar el
          //listado
          this.setThreadState(thread.key, {
            loading: false, truncated: false, messages: null, error: error.message
          });

          this.messageService.add({
            severity: 'warn', closable: true, summary: 'Buzón',
            detail: `No se ha podido completar la conversación. ${error.message}`
          });
        }
      });
  }

  private setThreadState(key: string, state: MailboxThreadState) {
    const conversations = new Map(this.conversations);
    conversations.set(key, state);
    this.conversations = conversations;
  }

  // ------------------------------------------------------------- listado

  loadMessages() {
    if (!this.mailAccountId) return;

    this.listLoading = true;
    this.listError = null;

    const page = Math.floor(this.first / this.rows) + 1;

    this.mailboxService
      .listMessages(this.mailAccountId, this.folderPath, page, this.rows, this.search)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.messages = result.items;
          this.totalRecords = result.total;
          this.uidValidity = result.uidValidity;
          this.listLoading = false;

          //las conversaciones traídas eran de la página anterior: pueden estar desfasadas y no valen para esta
          this.conversations = new Map();
        },
        error: (error: MailboxError) => {
          this.listError = error;
          this.messages = [];
          this.totalRecords = 0;
          this.listLoading = false;
        }
      });
  }

  onPageChanged(event: { first: number; rows: number }) {
    const page = Math.floor(event.first / event.rows) + 1;
    this.navigateWithQuery({ page: page > 1 ? page : null, rows: event.rows !== 50 ? event.rows : null }, true);
  }

  applySearch() {
    this.navigateWithQuery({ q: this.searchText || null, page: null }, true);
  }

  toggleSearchFilter(filter: 'unseen' | 'flagged' | 'body') {
    const current = this.route.snapshot.queryParamMap.get(filter) === '1';
    this.navigateWithQuery({ [filter]: current ? null : '1', page: null }, true);
  }

  clearSearch() {
    this.searchText = '';
    this.navigateWithQuery({ q: null, unseen: null, flagged: null, body: null, page: null }, true);
  }

  get hasSearch(): boolean {
    return !!this.search.text || !!this.search.unseenOnly || !!this.search.flaggedOnly;
  }

  // ------------------------------------------------------------- mensaje

  onMessageSelected(summary: MailboxMessageSummary) {
    //se vacía ya, sin esperar: dejar el mensaje anterior a la vista mientras carga el nuevo hace que la pantalla dé
    //un salto cuando llega, porque los dos miden distinto
    if (summary.uid !== this.selectedUid) {
      this.message = null;
      this.bodyDocument = null;
      this.bodyUrl = null;
      this.messageError = null;
      this.messageLoading = true;
    }

    this.router.navigate(['/mailbox', this.mailAccountId, encodeURIComponent(this.folderPath), summary.uid], {
      queryParamsHandling: 'preserve'
    });
  }

  closeMessage() {
    this.router.navigate(['/mailbox', this.mailAccountId, encodeURIComponent(this.folderPath)], {
      queryParamsHandling: 'preserve'
    });
  }

  private clearMessage() {
    this.message = null;
    this.bodyDocument = null;
    this.bodyUrl = null;
    this.sourceUrl = null;
    this.messageError = null;
    this.allowRemoteImages = false;
  }

  loadMessage(uid: number) {
    if (!this.mailAccountId) return;

    this.messageLoading = true;
    this.messageError = null;
    this.message = null;

    this.mailboxService
      .getMessage(this.mailAccountId, this.folderPath, uid, this.uidValidity, this.allowRemoteImages)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: message => {
          this.message = message;

          //la generación de la carpeta se toma del mensaje y no del listado: al abrir un correo por enlace
          //directo, listado y mensaje se piden a la vez y el valor del listado puede no haber llegado
          this.uidValidity = message.uidValidity || this.uidValidity;

          this.bodyDocument = message.bodyDocument || null;
          this.bodyUrl = this.mailboxService.getBodyUrl(
            this.mailAccountId!, this.folderPath, uid, this.uidValidity, this.allowRemoteImages);
          this.sourceUrl = this.mailboxService.getSourceUrl(this.mailAccountId!, this.folderPath, uid, this.uidValidity);
          this.messageLoading = false;

          //abrir un mensaje lo marca como leído, como en cualquier cliente de correo
          if (!message.seen) this.setFlags([uid], { seen: true });
        },
        error: (error: MailboxError) => {
          this.messageError = error;
          this.message = null;
          this.messageLoading = false;

          //el mensaje ya no está, o la carpeta cambió: el listado que tiene el usuario está obsoleto
          if (error.kind === 'notFound' || error.kind === 'staleFolder') this.loadMessages();
        }
      });
  }

  onAllowRemoteImages() {
    this.allowRemoteImages = true;
    if (this.selectedUid) this.loadMessage(this.selectedUid);
  }

  // ------------------------------------------------------------- acciones

  onToggleSeen(summary: MailboxMessageSummary | null = null) {
    const target = summary ?? this.message;
    if (!target) return;
    this.setFlags([target.uid], { seen: !target.seen });
  }

  onToggleFlagged(summary: MailboxMessageSummary | null = null) {
    const target = summary ?? this.message;
    if (!target) return;
    this.setFlags([target.uid], { flagged: !target.flagged });
  }

  private setFlags(uids: number[], flags: { seen?: boolean; flagged?: boolean }) {
    if (!this.mailAccountId) return;

    //se refleja en memoria antes de que responda el servidor, para que la interfaz vaya al momento. Si falla, se
    //deshace.
    const previos = new Map<number, { seen?: boolean; flagged?: boolean }>();
    for (const uid of uids) {
      const row = this.messages.find(item => item.uid === uid);
      const fuente = row ?? (this.message?.uid === uid ? this.message : null);
      if (!fuente) continue;

      previos.set(uid, { seen: fuente.seen, flagged: fuente.flagged });
      if (row) Object.assign(row, flags);
      if (this.message?.uid === uid) Object.assign(this.message, flags);
    }

    //el contador de la carpeta se ajusta aquí en lugar de volver a pedir el árbol entero: marcar como leído pasa
    //en cada correo que se abre, y una petición de todas las carpetas por clic no se justifica
    if (flags.seen !== undefined) this.adjustUnreadCount(uids.length, flags.seen);

    this.mailboxService
      .setFlags(this.mailAccountId, this.folderPath, this.uidValidity, uids, flags)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (error: MailboxError) => {
          //se deshace lo que se había pintado, y se avisa sin más.
          //
          //Antes esto ponía el error del listado y lo recargaba, que es desproporcionado: marcar como leído se
          //dispara solo al abrir cada correo, así que un fallo ahí tiraba la lista entera y la volvía a pedir. Y
          //con ella cambiaban los mensajes visibles.
          for (const [uid, valores] of previos) {
            const row = this.messages.find(item => item.uid === uid);
            if (row) Object.assign(row, valores);
            if (this.message?.uid === uid) Object.assign(this.message, valores);
          }
          if (flags.seen !== undefined) this.adjustUnreadCount(uids.length, !flags.seen);

          this.messageService.add({
            severity: 'warn', closable: true, summary: 'Buzón',
            detail: `No se ha podido cambiar la marca del mensaje. ${error.message}`
          });
        }
      });
  }

  /// Ajusta el contador de sin leer de la carpeta abierta, sin ir al servidor.
  private adjustUnreadCount(count: number, seen: boolean) {
    const ajustar = (list: MailboxFolder[]): boolean => {
      for (const folder of list ?? []) {
        if (folder.path === this.folderPath) {
          if (folder.unread !== null) folder.unread = Math.max(0, folder.unread + (seen ? -count : count));
          return true;
        }
        if (ajustar(folder.children ?? [])) return true;
      }
      return false;
    };

    //array nuevo para que el árbol se repinte
    if (ajustar(this.folders)) this.folders = [...this.folders];
  }

  onDeleteRequested(summary: MailboxMessageSummary | null = null) {
    const target = summary ?? this.message;
    if (!target || !this.mailAccountId) return;

    const trash = this.findSpecialFolder('trash');
    const inTrash = trash?.path === this.folderPath;

    //si no hay papelera, o ya estamos en ella, el borrado es definitivo: hay que decirlo con claridad porque no
    //hay vuelta atrás
    const permanent = !trash || inTrash;

    this.confirmationService.confirm({
      header: permanent ? 'Borrar definitivamente' : 'Enviar a la papelera',
      message: permanent
        ? '¿Borrar este mensaje de forma definitiva? No se podrá recuperar.'
        : '¿Enviar este mensaje a la papelera?',
      acceptLabel: permanent ? 'Borrar definitivamente' : 'Enviar a la papelera',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: permanent ? 'p-button-danger' : '',
      accept: () => this.performDelete(target.uid)
    });
  }

  private performDelete(uid: number) {
    if (!this.mailAccountId) return;

    this.mailboxService
      .delete(this.mailAccountId, this.folderPath, this.uidValidity, [uid])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (this.selectedUid === uid) this.closeMessage();
          this.loadMessages();
          this.refreshFolderCounts();
        },
        error: (error: MailboxError) => {
          this.listError = error;
          this.loadMessages();
        }
      });
  }

  onMoveRequested(targetFolder: string) {
    if (!this.message || !this.mailAccountId) return;
    const uid = this.message.uid;

    this.mailboxService
      .move(this.mailAccountId, this.folderPath, this.uidValidity, [uid], targetFolder)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeMessage();
          this.loadMessages();
          this.refreshFolderCounts();
        },
        error: (error: MailboxError) => {
          this.listError = error;
          this.loadMessages();
        }
      });
  }

  onAttachmentRequested(attachment: MailboxAttachment) {
    if (!this.mailAccountId || !this.message) return;

    //igual que el resto de descargas de la aplicación: se abre la URL, autenticada por la cookie de sesión
    const url = this.mailboxService.getPartUrl(
      this.mailAccountId, this.folderPath, this.message.uid, this.uidValidity, attachment.partId);
    window.open(url, '_blank');
  }

  /// Abre el compositor en blanco.
  composeNew() {
    this.composer?.openNew();
  }

  /// Pide al servidor el borrador de respuesta o reenvío y abre el compositor con él.
  ///
  /// La cita la monta el servidor porque es quien tiene el mensaje y quien sabe sanearlo antes de citarlo; hacerlo
  /// aquí obligaría a bajar el original y a volver a limpiarlo en el navegador.
  onComposeRequested(mode: MailboxComposeMode) {
    if (!this.mailAccountId || !this.message || mode === 'new') return;

    this.draftLoading = true;
    this.messageError = null;

    this.mailboxService
      .getDraft(this.mailAccountId, this.folderPath, this.message.uid, this.uidValidity, mode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: draft => {
          this.draftLoading = false;
          this.composer?.openWithDraft(draft);
        },
        error: (error: MailboxError) => {
          this.draftLoading = false;
          this.messageError = error;
        }
      });
  }

  /// Tras enviar: el listado puede haber cambiado (el original queda marcado como respondido o reenviado) y
  /// Enviados también.
  onSent() {
    this.loadMessages();
    if (this.selectedUid) this.loadMessage(this.selectedUid);
    this.refreshFolderCounts();
  }

  refresh() {
    this.loadFolders();
    this.loadMessages();
    if (this.selectedUid) this.loadMessage(this.selectedUid);
  }

  /// Recarga solo los contadores del árbol, sin tocar la carpeta abierta.
  private refreshFolderCounts() {
    if (!this.mailAccountId) return;
    this.mailboxService
      .getFolders(this.mailAccountId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: folders => (this.folders = folders),
        //los contadores son un adorno: si fallan no se molesta al usuario
        error: () => {}
      });
  }

  private findSpecialFolder(kind: string): MailboxFolder | null {
    const walk = (list: MailboxFolder[]): MailboxFolder | null => {
      for (const folder of list ?? []) {
        if (folder.specialUse === kind) return folder;
        const found = walk(folder.children ?? []);
        if (found) return found;
      }
      return null;
    };
    return walk(this.folders);
  }

  private navigateWithQuery(changes: Record<string, any>, replaceUrl: boolean) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: changes,
      queryParamsHandling: 'merge',
      replaceUrl
    });
  }
}
